/**
 * Track 2B — Shared Story Panels.
 *
 * Three construction-language panels for the operator's view:
 *
 *   • Open Stories      — anything not in a terminal state
 *   • Critical Stories  — conflict_open + waiting_for_clarification
 *                          + waiting_for_field (revision not acknowledged)
 *                          + waiting_for_review aging > 24h
 *   • Recently Closed   — last 7 days of closed/resolved stories
 *
 * Reused by:
 *   - Revision War Room  (project-scoped)
 *   - Superintendent Command View (project-scoped)
 *   - Operations Dashboard (project-scoped)
 *
 * One tap on a row → opens the shared StoryDrawer (What Happened Here)
 * or jumps to the drawing if the user prefers.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertOctagon, AlertTriangle, CheckCircle2, ChevronRight, Loader2,
  ExternalLink, Send,
} from "lucide-react";
import { api } from "@/lib/api";
import StoryDrawer, { KIND, STATUS_TONE, topKind } from "@/components/stories/StoryDrawer";


function _ageHours(iso) {
  if (!iso) return null;
  try { return (Date.now() - new Date(iso).getTime()) / 3600000; }
  catch { return null; }
}

const CRITICAL_STATUSES = new Set([
  "conflict_open", "waiting_for_clarification", "waiting_for_field",
]);


export default function StoryPanels({ projectId, testidPrefix = "story-panels", showRecentlyClosed = true }) {
  const [stories, setStories] = useState(null);
  const [err, setErr] = useState(null);
  const [drawerStoryId, setDrawerStoryId] = useState(null);

  const reload = useCallback(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/stories`)
      .then((r) => { setStories(r.data?.items || []); setErr(null); })
      .catch((e) => { setErr(e?.response?.data?.detail || "Couldn't load stories"); setStories([]); });
  }, [projectId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    const h = () => reload();
    window.addEventListener("forgedops:field-stories-changed", h);
    return () => window.removeEventListener("forgedops:field-stories-changed", h);
  }, [reload]);

  const buckets = useMemo(() => {
    const list = stories || [];
    const open      = list.filter((s) => !["closed", "resolved"].includes(s.status));
    const critical  = list.filter((s) =>
      CRITICAL_STATUSES.has(s.status)
      || (s.status === "waiting_for_review" && (_ageHours(s.last_activity_at) || 0) > 24)
    );
    const sevenDaysAgo = Date.now() - 7 * 86400000;
    const closed    = list.filter((s) =>
      ["closed", "resolved"].includes(s.status)
      && s.last_activity_at
      && new Date(s.last_activity_at).getTime() >= sevenDaysAgo,
    );
    // Critical-first sort for open/critical, newest-first for closed.
    const priorityOf = (s) => CRITICAL_STATUSES.has(s.status)
      ? 0 : s.status === "waiting_for_review" ? 1 : 2;
    open.sort((a, b) => priorityOf(a) - priorityOf(b)
      || (new Date(b.last_activity_at || 0) - new Date(a.last_activity_at || 0)));
    critical.sort((a, b) => priorityOf(a) - priorityOf(b)
      || (new Date(b.last_activity_at || 0) - new Date(a.last_activity_at || 0)));
    closed.sort((a, b) => new Date(b.last_activity_at || 0) - new Date(a.last_activity_at || 0));
    return { open, critical, closed };
  }, [stories]);

  if (!projectId) return null;
  if (stories === null && !err) {
    return (
      <div className="p-3 text-[var(--fop-text-3)] text-sm flex items-center gap-2" data-testid={`${testidPrefix}-loading`}>
        <Loader2 className="animate-spin" size={12} /> Loading stories…
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={testidPrefix}>
      <Panel
        title="Critical Stories"
        icon={<AlertOctagon size={12} className="text-[var(--st-escalated,#dc2626)]" />}
        rows={buckets.critical}
        testid={`${testidPrefix}-critical`}
        emptyText="No critical stories — field is current."
        onOpen={(s) => setDrawerStoryId(s.id)}
      />
      <Panel
        title="Open Stories"
        icon={<AlertTriangle size={12} className="text-[var(--fop-warning)]" />}
        rows={buckets.open}
        testid={`${testidPrefix}-open`}
        emptyText="No open stories on this project."
        onOpen={(s) => setDrawerStoryId(s.id)}
      />
      {showRecentlyClosed && (
        <Panel
          title="Recently Closed (7 days)"
          icon={<CheckCircle2 size={12} className="text-[var(--fop-success)]" />}
          rows={buckets.closed}
          testid={`${testidPrefix}-closed`}
          emptyText="No closures in the last 7 days."
          onOpen={(s) => setDrawerStoryId(s.id)}
          closedView
        />
      )}
      <StoryDrawer
        open={!!drawerStoryId}
        storyId={drawerStoryId}
        onClose={() => setDrawerStoryId(null)}
      />
    </div>
  );
}


function Panel({ title, icon, rows, testid, emptyText, onOpen, closedView }) {
  return (
    <section
      data-testid={testid}
      className="rounded border border-[var(--fop-border)] bg-[var(--fop-surface)] overflow-hidden"
    >
      <header className="px-3 py-1.5 border-b border-[var(--fop-border)] flex items-center gap-2 bg-[var(--fop-surface-2)]">
        {icon}
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--fop-text-2)]">{title}</div>
        <div className="ml-auto font-mono text-[10px] tabular-nums text-[var(--fop-text-3)]" data-testid={`${testid}-count`}>
          {rows.length}
        </div>
      </header>
      {rows.length === 0 ? (
        <div className="p-3 text-[12px] text-[var(--fop-text-3)]" data-testid={`${testid}-empty`}>
          {emptyText}
        </div>
      ) : (
        <ul>
          {rows.slice(0, 20).map((s) => (
            <StoryRow key={s.id} story={s} onOpen={onOpen} testid={`${testid}-row-${s.id}`} closedView={closedView} />
          ))}
        </ul>
      )}
    </section>
  );
}


function StoryRow({ story, onOpen, testid, closedView }) {
  const top = topKind(story);
  const meta = KIND[top] || KIND.photo;
  const Icon = meta.icon;
  const statusColour = STATUS_TONE[story.status] || meta.colour;
  const age = _ageHours(story.last_activity_at);
  const ageLabel = age == null ? "—"
    : age < 1 ? `${Math.round(age * 60)}m`
    : age < 24 ? `${Math.round(age)}h`
    : `${Math.round(age / 24)}d`;
  const lastActor = (story.people || [])[(story.people || []).length - 1];

  return (
    <li
      data-testid={testid}
      className="flex items-start gap-2 px-3 py-2 border-b border-[var(--fop-border)] hover:bg-[var(--fop-surface-2)] transition-colors cursor-pointer"
      onClick={() => onOpen(story)}
    >
      <div
        className="mt-0.5 inline-flex items-center justify-center rounded-full shrink-0"
        style={{ width: 22, height: 22, background: "var(--fop-surface)",
                 color: meta.colour, border: `2px solid ${meta.colour}66` }}
      >
        <Icon size={11} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold text-[var(--fop-text)] truncate">
          {meta.label} on {story.sheet_number || "this sheet"}
        </div>
        <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span
            className="px-1.5 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-[0.18em]"
            style={{ background: "rgba(0,0,0,0.04)", color: statusColour,
                     border: `1px solid ${statusColour}55` }}
            data-testid={`${testid}-status`}
          >
            {story.status_label || story.status || "Needs Attention"}
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--fop-text-3)]">
            {closedView ? "closed " : ""}{ageLabel}
          </span>
          {lastActor && (
            <span className="font-mono text-[10px] text-[var(--fop-text-3)]">· {lastActor}</span>
          )}
          {!closedView && (story.open_actions || []).length > 0 && (
            <span className="font-mono text-[10px] text-[var(--fop-text-3)]">
              · {(story.open_actions || []).length} open
            </span>
          )}
        </div>
      </div>
      <a
        href={`/sheet/${story.sheet_id}`}
        onClick={(e) => e.stopPropagation()}
        className="shrink-0 text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
        title="Jump to drawing"
        data-testid={`${testid}-jump`}
        aria-label="Jump to drawing"
      >
        <ExternalLink size={12} />
      </a>
      <ChevronRight size={12} className="text-[var(--fop-text-3)] shrink-0 mt-1 hidden" />
    </li>
  );
}
